import { defineConfig, createSystem, defaultConfig, Card, Flex, Text, Spinner, Badge, Button as Button$1 } from '@chakra-ui/react';
export { Avatar, Badge, Box, Card, Center, Button as ChakraButton, ChakraProvider, Container, Flex, Grid, HStack, Heading, IconButton, Image, Input, Separator, Spinner, Stack, Text, Textarea, VStack } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { jsx, jsxs } from 'react/jsx-runtime';

// src/theme/index.ts

// src/theme/colors.ts
var brandColors = {
  primary: "#4CAF50",
  primaryLight: "#81C784",
  primaryDark: "#388E3C",
  secondary: "#2196F3",
  accent: "#FF9800",
  success: "#4CAF50",
  warning: "#FF9800",
  error: "#F44336",
  info: "#2196F3"
};

// src/theme/tokens.ts
var spacing = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
  24: 96
};
var radii = {
  none: "0px",
  sm: "4px",
  md: "8px",
  lg: "12px",
  xl: "16px",
  "2xl": "24px",
  full: "9999px"
};
var fontSizes = {
  xs: "0.75rem",
  // 12px
  sm: "0.875rem",
  // 14px
  md: "1rem",
  // 16px
  lg: "1.125rem",
  // 18px
  xl: "1.25rem",
  // 20px
  "2xl": "1.5rem",
  // 24px
  "3xl": "1.875rem",
  // 30px
  "4xl": "2.25rem",
  // 36px
  "5xl": "3rem"
  // 48px
};
var fontWeights = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700
};
var lineHeights = {
  none: 1,
  tight: 1.25,
  snug: 1.375,
  normal: 1.5,
  relaxed: 1.625,
  loose: 2
};
var zIndices = {
  hide: -1,
  base: 0,
  docked: 10,
  dropdown: 1e3,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  toast: 1600,
  tooltip: 1700
};
var durations = {
  fast: "150ms",
  normal: "200ms",
  slow: "300ms",
  slower: "400ms"
};
var easings = {
  default: "cubic-bezier(0.4, 0, 0.2, 1)",
  linear: "linear",
  in: "cubic-bezier(0.4, 0, 1, 1)",
  out: "cubic-bezier(0, 0, 0.2, 1)",
  inOut: "cubic-bezier(0.4, 0, 0.2, 1)",
  spring: "cubic-bezier(0.175, 0.885, 0.32, 1.275)"
};
var breakpoints = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px"
};
var customConfig = defineConfig({
  theme: {
    tokens: {
      colors: {
        // Brand colors
        brand: {
          50: { value: "#E8F5E9" },
          100: { value: "#C8E6C9" },
          200: { value: "#A5D6A7" },
          300: { value: "#81C784" },
          400: { value: "#66BB6A" },
          500: { value: "#4CAF50" },
          600: { value: "#43A047" },
          700: { value: "#388E3C" },
          800: { value: "#2E7D32" },
          900: { value: "#1B5E20" }
        },
        // Mood colors
        mood: {
          joyful: { value: "#FFD93D" },
          content: { value: "#6BCB77" },
          neutral: { value: "#9E9E9E" },
          melancholic: { value: "#4D96FF" },
          anxious: { value: "#FF6B6B" },
          excited: { value: "#FF9F43" },
          grateful: { value: "#A78BFA" },
          nostalgic: { value: "#F472B6" }
        }
      },
      fonts: {
        heading: { value: '"Inter", system-ui, sans-serif' },
        body: { value: '"Inter", system-ui, sans-serif' }
      },
      radii: {
        card: { value: "16px" },
        button: { value: "12px" },
        input: { value: "8px" }
      },
      spacing: {
        safe: { value: "env(safe-area-inset-bottom, 0px)" }
      }
    },
    semanticTokens: {
      colors: {
        // Background colors
        "bg.primary": {
          value: { _light: "{colors.white}", _dark: "{colors.gray.900}" }
        },
        "bg.secondary": {
          value: { _light: "{colors.gray.50}", _dark: "{colors.gray.800}" }
        },
        "bg.muted": {
          value: { _light: "{colors.gray.100}", _dark: "{colors.gray.700}" }
        },
        // Text colors
        "text.primary": {
          value: { _light: "{colors.gray.900}", _dark: "{colors.white}" }
        },
        "text.secondary": {
          value: { _light: "{colors.gray.600}", _dark: "{colors.gray.400}" }
        },
        "text.muted": {
          value: { _light: "{colors.gray.500}", _dark: "{colors.gray.500}" }
        },
        // Border colors
        "border.default": {
          value: { _light: "{colors.gray.200}", _dark: "{colors.gray.700}" }
        },
        "border.muted": {
          value: { _light: "{colors.gray.100}", _dark: "{colors.gray.800}" }
        }
      }
    }
  }
});
var system = createSystem(defaultConfig, customConfig);
var MotionCard = motion.create(Card.Root);
function WelcomeCard({ title, message, status }) {
  const statusColor = {
    loading: "yellow",
    connected: "green",
    error: "red"
  }[status];
  const statusText = {
    loading: "Connecting...",
    connected: "Connected",
    error: "Connection Failed"
  }[status];
  return /* @__PURE__ */ jsx(
    MotionCard,
    {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.4, ease: "easeOut" },
      maxW: "md",
      w: "full",
      borderRadius: "xl",
      overflow: "hidden",
      bg: "bg.panel",
      borderWidth: "1px",
      borderColor: "border.default",
      shadow: "lg",
      children: /* @__PURE__ */ jsx(Card.Body, { p: 6, children: /* @__PURE__ */ jsxs(Flex, { direction: "column", align: "center", gap: 4, textAlign: "center", children: [
        /* @__PURE__ */ jsx(Text, { fontSize: "2xl", fontWeight: "bold", color: "fg.default", children: title }),
        /* @__PURE__ */ jsx(Text, { fontSize: "md", color: "fg.muted", children: message }),
        /* @__PURE__ */ jsxs(Flex, { align: "center", gap: 2, mt: 2, children: [
          status === "loading" && /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
          /* @__PURE__ */ jsx(Badge, { colorPalette: statusColor, size: "lg", children: statusText })
        ] })
      ] }) })
    }
  );
}
function Button({
  children,
  onClick,
  disabled = false,
  loading = false,
  type = "button"
}) {
  return /* @__PURE__ */ jsx(
    Button$1,
    {
      type,
      onClick,
      disabled: disabled || loading,
      colorPalette: "blue",
      size: "lg",
      minH: "44px",
      px: 6,
      fontWeight: "semibold",
      borderRadius: "lg",
      children: loading ? /* @__PURE__ */ jsx(Spinner, { size: "sm" }) : children
    }
  );
}

// src/animations/index.ts
var fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.3 }
};
var fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: "easeOut" }
};
var fadeInDown = {
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: "easeOut" }
};
var scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.3, ease: "easeOut" }
};
var slideInLeft = {
  initial: { opacity: 0, x: -30 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.4, ease: "easeOut" }
};
var slideInRight = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.4, ease: "easeOut" }
};
var staggerContainer = {
  initial: "hidden",
  animate: "show",
  variants: {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }
};
var staggerItem = {
  variants: {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  }
};
var pageTransition = {
  initial: {
    opacity: 0,
    y: 20
  },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
      ease: "easeIn"
    }
  }
};
var overlayTransition = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.15
    }
  }
};
var modalTransition = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 20
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
    transition: {
      duration: 0.2
    }
  }
};
var springConfig = {
  type: "spring",
  stiffness: 300,
  damping: 30
};
var tapAnimation = {
  scale: 0.98,
  transition: { duration: 0.1 }
};
var hoverAnimation = {
  scale: 1.02,
  transition: { duration: 0.2 }
};
var wrappedSlideTransition = {
  enter: (direction) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: (direction) => ({
    x: direction < 0 ? 300 : -300,
    opacity: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1]
    }
  })
};
var revealAnimation = {
  hidden: {
    opacity: 0,
    scale: 0.8,
    y: 30
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.34, 1.56, 0.64, 1]
      // Bouncy ease
    }
  }
};
var countUpConfig = {
  stiffness: 50,
  damping: 30
};

export { Button, WelcomeCard, brandColors, breakpoints, countUpConfig, durations, easings, fadeIn, fadeInDown, fadeInUp, fontSizes, fontWeights, hoverAnimation, lineHeights, modalTransition, overlayTransition, pageTransition, radii, revealAnimation, scaleIn, slideInLeft, slideInRight, spacing, springConfig, staggerContainer, staggerItem, system, tapAnimation, wrappedSlideTransition, zIndices };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map